import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "@/hooks/LoaderProvider";
import { PlusOutlined } from "@/config/antd";
import {
  fetchSupportTicketsAsync,
  fetchTicketsByAny,
  selectAllTickets,
  selectFetchedTickets,
  selectSupportIsLoading,
  setPagination,
  selectPagination,
  selectStatusUpdateSuccess,
  clearStatusUpdateSuccess,
} from "@/store/redux/slices/support/support_slice";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import SupportCard from "@/pages/support/components/Support_Card";
import AddTicketModal from "@/pages/support/Add_Ticket_Modal";
import SupportHomeTable from "@/pages/support/components/SupportHomeTable";
import EmptyState from "@/pages/support/components/Empty_State";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { getOrganizationId } from "@/utils/token_functions";

const SupportHome = () => {
  const dispatch = useDispatch();
  const tickets = useSelector(selectAllTickets);
  const fetchedTickets = useSelector(selectFetchedTickets);
  const isLoading = useSelector(selectSupportIsLoading);
  useSelector(selectPagination);

  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();

  const [showAddModal, setShowAddModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  // Add a selector for status update success
  const statusUpdateSuccess = useSelector(selectStatusUpdateSuccess);

  const getTicketsToUse = useCallback(() => {
    return fetchedTickets && fetchedTickets.length > 0
      ? fetchedTickets
      : tickets;
  }, [fetchedTickets, tickets]);

  // Format tickets for the table/cards
  // Transform and filter data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getTicketsToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.subject?.toLowerCase().includes(searchValue.toLowerCase()) ||
          ticket.id?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "open":
        filtered = filtered.filter((ticket) => ticket.status === "Open");
        break;
      case "closed":
        filtered = filtered.filter((ticket) => ticket.status === "Closed");
        break;
      case "pending":
        filtered = filtered.filter((ticket) => ticket.status === "Pending");
        break;
      default:
        break;
    }

    return filtered.map((ticket) => {
      let dateObj = new Date(ticket.createdAt || ticket.updatedAt);

      return {
        key: ticket._id,
        id: ticket._id,
        ticket_id: ticket.id,
        description: ticket.subject,
        date:
          dateObj.toLocaleDateString() !== "Invalid Date"
            ? dateObj.toLocaleDateString()
            : "N/A",
        time:
          dateObj.toLocaleTimeString() !== "Invalid Date"
            ? dateObj.toLocaleTimeString()
            : "N/A",
        status: ticket.status,
        type: ticket.priority,
      };
    });
  }, [getTicketsToUse, searchValue, activeFilter]);

  // Transform and filter data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getTicketsToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.subject?.toLowerCase().includes(searchValue.toLowerCase()) ||
          ticket.id?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "open":
        filtered = filtered.filter((ticket) => ticket.status === "Open");
        break;
      case "closed":
        filtered = filtered.filter((ticket) => ticket.status === "Closed");
        break;
      case "pending":
        filtered = filtered.filter((ticket) => ticket.status === "Pending");
        break;
      default:
        break;
    }

    return filtered.map((ticket) => {
      let dateObj = new Date(ticket.createdAt || ticket.updatedAt);

      return {
        key: ticket._id,
        id: ticket._id,
        ticket_id: ticket.id,
        description: ticket.subject,
        date:
          dateObj.toLocaleDateString() !== "Invalid Date"
            ? dateObj.toLocaleDateString()
            : "N/A",
        time:
          dateObj.toLocaleTimeString() !== "Invalid Date"
            ? dateObj.toLocaleTimeString()
            : "N/A",
        status: ticket.status,
        type: ticket.priority,
      };
    });
  }, [getTicketsToUse, searchValue, activeFilter]);

  
  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("Today");

    // 5. Fetch fresh data
    showLoader();
    try {
      const organizationId = getOrganizationId();

      if (organizationId) {
        await dispatch(
          fetchTicketsByAny({
            key: "organizationID",
            value: organizationId,
          })
        ).unwrap();
      } else {
        await dispatch(
          fetchSupportTicketsAsync({
            skip: 0, // Reset to first page
            limit: pageSize,
          })
        ).unwrap();
      }
    } catch (error) {
      console.error("Error refreshing support tickets:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, pageSize, showLoader, hideLoader]);

  useEffect(() => {
    const fetchTickets = async () => {
      showLoader();
      try {
        const organizationId = getOrganizationId();

        if (organizationId) {
          try {
            // First try to fetch by organization ID
            await dispatch(
              fetchTicketsByAny({
                key: "organizationID",
                value: organizationId,
              })
            ).unwrap();
          } catch (error) {
            console.error("Error fetching tickets by organization:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching support tickets:", error);
      } finally {
        hideLoader();
      }
    };

    fetchTickets();
  }, [dispatch, hideLoader, showLoader]);

  // Listen for status updates and refresh data when needed
  useEffect(() => {
    if (statusUpdateSuccess) {
      // When a ticket status is updated successfully, refresh the data
      handleRefresh();
      // Then clear the success flag
      dispatch(clearStatusUpdateSuccess());
    }
  }, [statusUpdateSuccess, dispatch, handleRefresh]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };


  const handleFilterChange = (value) => {
    setFilterValue(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(
      setPagination({
        skip: (page - 1) * pageSize,
        limit: pageSize,
      })
    );
  };

  const filterOptions = {
    items: [
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  // Get filtered data
  const filteredTableData = getFilteredTableData();
  const filteredCardsData = getFilteredCardsData();
  const ticketsToUse = getTicketsToUse();

  const showEmptyState = () => {
    if (
      !isLoading &&
      (!ticketsToUse ||
        ticketsToUse.length === 0 ||
        filteredTableData.length === 0)
    ) {
      let message = "No Support Tickets Found";

      if (searchValue.trim()) {
        message = "No tickets found matching your search";
      } else if (activeFilter !== "all") {
        message = `No ${activeFilter} tickets found`;
      }

      return <EmptyState onAddNew={handleAddClick} message={message} />;
    }
    return null;
  };

  // Return null if loading to show the global loader
  if (isLoading && ticketsToUse.length === 0) {
    return null;
  }

  // Define breadcrumb items
  const breadcrumbItems = [{ title: "Support", link: "/dashboard/support" }];

  // Calculate ticket counts
  const openCount =
    ticketsToUse?.filter((ticket) => ticket.status === "Open")?.length || 0;
  const closedCount =
    ticketsToUse?.filter((ticket) => ticket.status === "Closed")?.length || 0;
  const pendingCount =
    ticketsToUse?.filter((ticket) => ticket.status === "Pending")?.length || 0;
  const totalCount = ticketsToUse?.length || 0;

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Tickets" },
    { key: "open", label: "Open" },
    { key: "closed", label: "Closed" },
    { key: "pending", label: "Pending" },
  ];

  return (
    <div className="w-full">
      <div>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <PageHeader title="Support" />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1 className="heading-medium text-lighterGrey">
              {totalCount} Total Tickets
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Tickets
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Tickets (${totalCount})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Open (${openCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "open" ? "primary" : "white"}
              customTextColor={activeFilter === "open" ? "white" : "primary"}
              onClick={() => setActiveFilter("open")}
            />
            <Button
              text={`Closed (${closedCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "closed" ? "primary" : "white"}
              customTextColor={activeFilter === "closed" ? "white" : "primary"}
              onClick={() => setActiveFilter("closed")}
            />
            <Button
              text={`Pending (${pendingCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "pending" ? "primary" : "white"}
              customTextColor={activeFilter === "pending" ? "white" : "primary"}
              onClick={() => setActiveFilter("pending")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text="ADD NEW TICKET"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            iconPosition="left"
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8 whitespace-nowrap" // Added size adjustments
            customBgColor="white"
            customTextColor="primary"
          />
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddClick}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      <>
        {showEmptyState() || (
          <>
            <div className="hidden lg:block lg:border-2 border-[#eee] rounded-xl">
              {filteredTableData.length > 0 && (
                <div className="flex-col hidden p-5 text-left lg:flex">
                  <h1 className="mb-2 font-bold text-regular text-lighterGrey">
                    List of support tickets raised between you and Veegil
                    support team
                  </h1>
                  <p className="text-small text-lighterGrey">
                    From the list, you view all tickets and their status whether
                    they have been closed or still open.
                  </p>
                </div>
              )}
              <SupportHomeTable
                data={filteredTableData}
                setSelectedEvents={() => {}}
                isLoading={isLoading}
                currentPage={currentPage}
                pageSize={pageSize}
                total={totalCount}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
              {filteredCardsData.map((ticket) => {
                // Extract key/id from ticket and rest of props separately
                const { key, ...ticketProps } = ticket;

                return (
                  <SupportCard
                    key={ticket.id || key} // Pass key directly, not in the spread
                    {...ticketProps} // Spread the rest of the props
                    className="gap-4"
                  />
                );
              })}
            </div>

            {filteredTableData.length > 0 && (
              <div className="mt-6">
                <CustomPagination
                  current={currentPage}
                  total={totalCount}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  className="pb-8"
                />
              </div>
            )}
          </>
        )}
      </>

      <AddTicketModal
        isVisible={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
    </div>
  );
};

export default SupportHome;
